import React, { FC, useMemo, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { FlexAlign, FlexJustify } from '@src/types';
import { Flexbox } from '@src/views/containers';
import SOWTab from './SOWTab';
import SOWSelector from './SOWSelector';
import DocumentVersionMini from './DocumentVersionMini';
import { getLocalToday, pickDefaultSow } from './sowOutlineUtils';
import type { SOWOutline } from './ClientOutlineTypes';

const SOW_TAB_DOCUMENT_TYPE_ID = '2c4b307c-d8c9-4eb2-899b-8bc548b38775';

interface SOWTabMenuProps {
    clientId: string;
    sows: SOWOutline[];
    onOpenAddSowDialog?: () => void;
    onOpenEditSowDialog?: ( sow: SOWOutline ) => void;
    onOpenAddOfferingDialog: ( sowId: string, sowName: string ) => void;
}

const SOWTabMenu: FC<SOWTabMenuProps> = ( { clientId, sows, onOpenAddSowDialog, onOpenEditSowDialog, onOpenAddOfferingDialog } ) => {
    const sectionIndent = 16;
    const today = useMemo( getLocalToday, [] );

    const [ selectedSowId, setSelectedSowId ] = useState( () => pickDefaultSow( sows, today )?.SOWId ?? '' );

    // Falls back to the default when the outline refreshes without the selected SOW.
    const selectedSow = sows.find( sow => sow.SOWId === selectedSowId ) ?? pickDefaultSow( sows, today );
    const canAddSow = !!clientId && !!onOpenAddSowDialog;

    return (
        <div className={ 'sow-section' } style={ { paddingLeft: sectionIndent } }>
            <div className={ 'sow-detail-card' }>
                <Flexbox
                    align={ FlexAlign.CENTER }
                    justify={ FlexJustify.START }
                    noWrap
                    className={ 'sow-section-header' }
                >
                    <span className={ 'sow-section-label' }>
                        SOW <span className={ 'sow-section-count' }>({ sows.length })</span>
                    </span>

                    { selectedSow && (
                        <SOWSelector
                            sows={ sows }
                            selectedSow={ selectedSow }
                            today={ today }
                            onSelect={ setSelectedSowId }
                        />
                    ) }

                    <Button
                        type={ 'button' }
                        className={ 'dash-add-btn sow-section-add' }
                        onClick={ onOpenAddSowDialog }
                        disabled={ !canAddSow }
                    >
                        <Icon name={ 'plus' } />
                        New SOW
                    </Button>
                </Flexbox>

                { selectedSow ? (
                    <div key={ selectedSow.SOWId } className={ 'sow-detail-body' }>
                        <SOWTab
                            clientId={ clientId }
                            sow={ selectedSow }
                            onOpenEditSowDialog={ onOpenEditSowDialog }
                            onOpenAddOfferingDialog={ onOpenAddOfferingDialog }
                            headerAside={
                                <DocumentVersionMini
                                    clientId={ clientId }
                                    sowId={ selectedSow.SOWId }
                                    documentTypeId={ SOW_TAB_DOCUMENT_TYPE_ID }
                                    FileDisplayName={ selectedSow.SOWName }
                                    documentItemId={ selectedSow.DocumentItemId }
                                />
                            }
                        />
                    </div>
                ) : (
                    <div className={ 'document-version-empty' }>No SOWs yet.</div>
                ) }
            </div>
        </div>
    );
};

export default SOWTabMenu;
